'use client';

import { Button, Input } from '@commenda/kakashi';
import { Plus, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

/**
 * Extracts all field paths from an object using dot notation.
 * e.g., { value: 'x', metadata: { mandatory: true } } => ['value', 'metadata', 'metadata.mandatory']
 */
export function extractFieldPaths(
  obj: Record<string, unknown>,
  prefix = ''
): string[] {
  const paths: string[] = [];
  Object.keys(obj).forEach((key) => {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    paths.push(fullPath);
    const value = obj[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      paths.push(
        ...extractFieldPaths(value as Record<string, unknown>, fullPath)
      );
    }
  });
  return paths;
}

export function StaticArrayInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[] | string | undefined;
  onChange: (value: string[]) => void;
  placeholder?: string;
}) {
  const [newValue, setNewValue] = useState('');

  const items: string[] = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      return [value];
    }
    return [];
  }, [value]);

  const addItem = () => {
    if (newValue.trim()) {
      onChange([...items, newValue.trim()]);
      setNewValue('');
    }
  };

  const removeItem = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="flex items-center gap-1 px-2 py-1 bg-bg-tertiary rounded-md"
            >
              <span className="txt-small-regular text-text-primary">
                {item}
              </span>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-text-tertiary hover:text-text-primary"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (newValue.trim()) {
              addItem();
            }
          }}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addItem}
          disabled={!newValue.trim()}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <p className="txt-xs-regular text-text-quaternary">
        Press Enter or click + to add each value
      </p>
    </div>
  );
}
